import * as React from "react";
import { Card, Tabs } from "antd";
import { WorkloadContainerImage } from "./container/image";
import { WorkloadContainerResources } from "./container/resources";
import { WorkloadContainerGPU } from "./container/gpu";
import { WorkloadContainerEnvVars } from "./container/envVars";
import { WorkloadContainerVolumes } from "./container/volumes";
import { WorkloadContainerMetrics } from "./container/metrics";
import { WorkloadContainerHooks } from "./container/hooks";
import { WorkloadContainerRuntime } from "./container/runtime";
import { CardContent } from "../../../components/antd/card/CardContent";
import { WorkloadContainerProbe } from "./container/probe/probe";
import { formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { TabLabel } from "./tabLabel";
import { useCpFormContext } from "../../../components/form/useCpFormContext";

interface Props {
  index: number;
}
export const WorkloadContainerTabs: React.FC<Props> = React.memo(({ index }) => {
  const { id } = useCpFormContext();
  const type = useCpFormValue(`type`);
  const activeTabKey: string = useCpFormValue(`containers.${index}.activeTabKey`);

  const onChange = (newActiveKey: string) => {
    formSetValue(id, `containers.${index}.activeTabKey`, newActiveKey);
  };

  return (
    <div className="cpln-form-section cpln-ant-tab-content">
      <Card>
        <CardContent>
          <Tabs
            className="inner-tabs"
            items={[
              {
                key: `container-${index}-image`,
                label: (
                  <TabLabel
                    label="Image & Name"
                    names={[
                      `containers.${index}.image`,
                      `containers.${index}.name`,
                      `containers.${index}.servesTraffic`,
                      `containers.${index}.ports`,
                    ]}
                  />
                ),
                children: (
                  <div className="cpln-form-section cpln-ant-tab-content mt-6">
                    <WorkloadContainerImage index={index} />
                  </div>
                ),
              },
              {
                key: `container-${index}-resources`,
                label: (
                  <TabLabel
                    label="Resources"
                    names={[
                      `containers.${index}.cpu`,
                      `containers.${index}.memory`,
                      `containers.${index}.minCpu`,
                      `containers.${index}.minMemory`,
                      `containers.${index}.gpu`,
                    ]}
                  />
                ),
                children: (
                  <div className="cpln-form-section cpln-ant-tab-content mt-6">
                    <WorkloadContainerResources index={index} />
                    <WorkloadContainerGPU index={index} />
                  </div>
                ),
              },
              {
                key: `container-${index}-env-vars`,
                label: <TabLabel label="Env Vars" names={[`containers.${index}.env`]} />,
                children: (
                  <div className="cpln-form-section cpln-ant-tab-content mt-6">
                    <WorkloadContainerEnvVars index={index} />
                  </div>
                ),
              },
              {
                key: `container-${index}-volumes`,
                label: <TabLabel label="Volumes" names={[`containers.${index}.volumes`]} />,
                children: (
                  <div className="cpln-form-section cpln-ant-tab-content mt-6">
                    <WorkloadContainerVolumes index={index} />
                  </div>
                ),
              },
              ...(type !== "cron"
                ? [
                    {
                      key: `container-${index}-probes`,
                      label: (
                        <TabLabel
                          label="Probes"
                          names={[`containers.${index}.readinessProbe`, `containers.${index}.livenessProbe`]}
                        />
                      ),
                      children: (
                        <div className="cpln-form-section cpln-ant-tab-content mt-6">
                          <WorkloadContainerProbe index={index} property="readiness" />
                          <WorkloadContainerProbe index={index} property="liveness" />
                        </div>
                      ),
                    },
                  ]
                : []),
              {
                key: `container-${index}-advanced-options`,
                label: (
                  <TabLabel
                    label="Advanced Options"
                    names={[
                      `containers.${index}.metrics`,
                      `containers.${index}.lifecycle`,
                      `containers.${index}.workingDir`,
                      `containers.${index}.command`,
                      `containers.${index}.args`,
                    ]}
                  />
                ),
                children: (
                  <div className="cpln-form-section cpln-ant-tab-content mt-6">
                    <WorkloadContainerMetrics index={index} />
                    <WorkloadContainerHooks index={index} />
                    <WorkloadContainerRuntime index={index} />
                  </div>
                ),
              },
            ]}
            onChange={onChange}
            activeKey={activeTabKey}
          />
        </CardContent>
      </Card>
    </div>
  );
});
